import type React from 'react';
import { useCallback, useContext } from 'react';
import LeftNavigationContext from '../LeftNavigationContext.js';

type UseExpandableOnClickHandlerProps = {
  id: string;
  onExpand: React.MouseEventHandler & React.KeyboardEventHandler;
  onOpen: React.MouseEventHandler & React.KeyboardEventHandler;
  selectable?: boolean;
};

export const useExpandableOnClickHandler = ({
  id,
  onExpand,
  onOpen,
  selectable = true,
}: UseExpandableOnClickHandlerProps): ((e: React.MouseEvent<HTMLElement>) => void) => {
  const {
    leftNavProps: { expanded },
    setSelectedItem,
  } = useContext(LeftNavigationContext);

  const handleOnExpand: React.MouseEventHandler = useCallback(
    (e) => {
      onExpand(e);
      if (selectable) setSelectedItem(id);
    },
    [id, onExpand, setSelectedItem, selectable],
  );

  const handleOnOpen: React.MouseEventHandler = useCallback(
    (e) => {
      onOpen(e);
      if (selectable) setSelectedItem(id);
    },
    [id, onOpen, setSelectedItem, selectable],
  );

  const handleOnClick: React.MouseEventHandler = useCallback(
    (e) => {
      if (!expanded) handleOnExpand(e);
      else handleOnOpen(e);
    },
    [expanded, handleOnExpand, handleOnOpen],
  );

  return handleOnClick;
};
